// src/pages/AdminContact.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Mail, Trash2, Eye, RefreshCw } from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { safeEmail } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const font = { mono: '"JetBrains Mono", monospace', condensed: '"Barlow Condensed", sans-serif', body: '"Barlow", sans-serif' };

const TYPE_LABELS = {
  general:     'General',
  partnership: 'Partnership',
  community:   'Community',
  press:       'Press',
};

const AdminContact = () => {
  const { authToken } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [toast,    setToast]    = useState('');

  const headers = { Authorization: `Bearer ${authToken}` };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/contact-messages`, { headers })
      .then(r => r.json())
      .then(d => setMessages(d.messages || []))
      .catch(() => showToast('Failed to load messages'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await fetch(`${BACKEND_URL}/api/admin/contact-messages/${id}/read`, { method: 'PATCH', headers });
    setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m));
    if (selected?.id === id) setSelected(s => ({ ...s, is_read: true }));
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await fetch(`${BACKEND_URL}/api/admin/contact-messages/${id}`, { method: 'DELETE', headers });
    setMessages(prev => prev.filter(m => m.id !== id));
    if (selected?.id === id) setSelected(null);
    showToast('Message deleted');
  };

  const openMessage = (msg) => {
    setSelected(msg);
    if (!msg.is_read) markRead(msg.id);
  };

  const unread = messages.filter(m => !m.is_read).length;

  const S = {
    page:    { display: 'flex', height: '100vh', overflow: 'hidden' },
    list:    { width: 340, borderRight: '1px solid #111', overflowY: 'auto', flexShrink: 0 },
    detail:  { flex: 1, overflowY: 'auto', padding: '2rem' },
    row:     (active, unread) => ({
      padding: '0.9rem 1.25rem',
      borderBottom: '1px solid #0d0d0d',
      cursor: 'pointer',
      background: active ? '#111' : (unread ? '#080808' : '#000'),
      borderLeft: active ? '2px solid #fff' : '2px solid transparent',
      transition: 'all 0.1s',
    }),
    btn:     { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.3rem 0.7rem', fontFamily: font.mono, fontSize: '0.6rem', letterSpacing: '0.08em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 },
  };

  return (
    <div style={S.page}>
      <style>{`@keyframes fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {toast && (
        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: '#111', border: '1px solid #222', color: '#fff', fontFamily: font.mono, fontSize: '0.7rem', padding: '0.75rem 1.25rem', zIndex: 999, animation: 'fadein 0.2s ease' }}>
          {toast}
        </div>
      )}

      {/* Message list */}
      <div style={S.list}>
        <div style={{ padding: '1.25rem', borderBottom: '1px solid #111', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#333', letterSpacing: '0.2em' }}>// INBOX</div>
            <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1.4rem', marginTop: 2 }}>
              Contact
              {unread > 0 && (
                <span style={{ marginLeft: 8, background: '#fff', color: '#000', fontFamily: font.mono, fontSize: '0.55rem', fontWeight: 700, padding: '2px 7px', verticalAlign: 'middle' }}>
                  {unread} NEW
                </span>
              )}
            </div>
          </div>
          <button style={S.btn} onClick={load}><RefreshCw size={11} /></button>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', fontFamily: font.mono, fontSize: '0.65rem', color: '#333', textAlign: 'center' }}>Loading...</div>
        ) : messages.length === 0 ? (
          <div style={{ padding: '3rem 1.5rem', textAlign: 'center' }}>
            <Mail size={28} color="#222" style={{ margin: '0 auto 0.75rem' }} />
            <p style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#333' }}>No messages yet</p>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} style={S.row(selected?.id === msg.id, !msg.is_read)} onClick={() => openMessage(msg)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                {!msg.is_read && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', flexShrink: 0 }} />}
                <span style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#555', letterSpacing: '0.1em', marginLeft: msg.is_read ? 12 : 0 }}>
                  {TYPE_LABELS[msg.contact_type] || msg.contact_type}
                </span>
                <span style={{ fontFamily: font.mono, fontSize: '0.55rem', color: '#333', marginLeft: 'auto' }}>
                  {new Date(msg.created_at).toLocaleDateString()}
                </span>
              </div>
              <div style={{ fontFamily: font.body, fontSize: '0.85rem', color: msg.is_read ? '#666' : '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                {msg.subject}
              </div>
              <div style={{ fontFamily: font.body, fontSize: '0.75rem', color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {msg.name} · {msg.email}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail pane */}
      <div style={S.detail}>
        {!selected ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#222' }}>
            <Mail size={40} />
            <p style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#333', marginTop: '1rem', letterSpacing: '0.1em' }}>
              SELECT A MESSAGE
            </p>
          </div>
        ) : (
          <div style={{ maxWidth: 680 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid #111' }}>
              <div>
                <span style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#555', letterSpacing: '0.12em', background: '#111', padding: '2px 8px', display: 'inline-block', marginBottom: '0.75rem' }}>
                  {TYPE_LABELS[selected.contact_type] || selected.contact_type}
                </span>
                <h2 style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '1.8rem', margin: 0, lineHeight: 1.1, marginBottom: '0.4rem' }}>
                  {selected.subject}
                </h2>
                <div style={{ fontFamily: font.mono, fontSize: '0.65rem', color: '#555' }}>
                  {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: '1rem' }}>
                {!selected.is_read && (
                  <button style={S.btn} onClick={() => markRead(selected.id)}>
                    <Eye size={11} /> Mark Read
                  </button>
                )}
                <button style={{ ...S.btn, color: '#553333', borderColor: '#1a0a0a' }} onClick={() => deleteMsg(selected.id)}>
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>

            {/* Sender */}
            <div style={{ background: '#080808', border: '1px solid #111', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', gap: '2rem' }}>
              <div>
                <div style={{ fontFamily: font.mono, fontSize: '0.55rem', color: '#444', letterSpacing: '0.12em', marginBottom: 3 }}>FROM</div>
                <div style={{ fontFamily: font.body, fontSize: '0.9rem', color: '#ddd' }}>{selected.name}</div>
              </div>
              <div>
                <div style={{ fontFamily: font.mono, fontSize: '0.55rem', color: '#444', letterSpacing: '0.12em', marginBottom: 3 }}>EMAIL</div>
                <a href={safeEmail(selected.email) ? `mailto:${safeEmail(selected.email)}` : '#'} style={{ fontFamily: font.mono, fontSize: '0.75rem', color: '#888', textDecoration: 'none' }}>
                  {selected.email}
                </a>
              </div>
            </div>

            {/* Body */}
            <div style={{ fontFamily: font.body, fontWeight: 300, fontSize: '1rem', color: '#ccc', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
              {selected.message}
            </div>

            {/* Reply shortcut */}
            <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #111' }}>
              <a
                href={safeEmail(selected.email) ? `mailto:${safeEmail(selected.email)}?subject=Re: ${encodeURIComponent(selected.subject || '')}` : '#'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: '#000', fontFamily: font.mono, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', padding: '0.6rem 1.25rem', textDecoration: 'none' }}
              >
                <Mail size={13} /> REPLY VIA EMAIL
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContact;
